import React, { useState } from 'react';
import { SortableContainer, SortableElement } from 'react-sortable-hoc';

import Axios from 'axios';

import arrayMove from 'array-move';
import styled from 'styled-components';

import { getBookCover } from '@libs/NwUtils';

import { useLayoutContext } from '@contexts/LayoutContext';

import { useDeletePlaylistFromPackage, useActivePackages } from '@hooks/API/Packages/UsePackages';

import BookEditorDrawer from '@components/ContactDetails/ModelDetails/CommonComponents/ModelDetailsBooks/BookEditorDrawer';

import NwDrawer from '@components/Gui/NwDrawer';
import NwIcon from '@components/Gui/NwIcon';
import NwTimesButton from '@components/Gui/NwTimesButton';

import { usePackageContext } from '../PackageContext';
import PackageMediaDrawer from '../PackageMediaDrawer';
import PackagePlayListDrawer from '../PackagePlayListDrawer';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

/** Il tasto di rimozione è più grande su mobile per restare un target toccabile. */
const REMOVE_BUTTON_SIZE_DESKTOP = 24;
const REMOVE_BUTTON_SIZE_MOBILE = 28;

/**
 * react-sortable-hoc ascolta il mousedown/touchstart nativo sul container, quindi
 * fermare la propagazione lato React non basta: il drag va annullato qui.
 * Il controllo di default della libreria guarda solo `event.target.tagName`, che
 * su un pulsante con icona è l'`svg` interno e non il `button`.
 */
const shouldCancelSortStart = (event) => {
    return Boolean(event.target.closest && event.target.closest('button'));
};

const PlaylistsGrid = styled.div`
    display: grid;
    /* colonne a larghezza fissa: con pochi book le tile non si dilatano */
    grid-template-columns: repeat(auto-fill, ${(props) => props.$tileWidth}px);
    gap: 1rem 0.75rem;
    align-items: start;

    @media only screen and (max-width: 720px) {
        /* su schermo stretto le colonne si allargano per riempire la riga */
        grid-template-columns: repeat(auto-fill, minmax(${(props) => props.$tileWidth}px, 1fr));
    }
`;

const PlaylistTileWrapper = styled.div`
    min-width: 0;
    /* z-index inerte sull'elemento in flusso, ma il clone che react-sortable-hoc
       appende al body lo eredita: serve a tenere la tile trascinata sopra il drawer */
    z-index: 2100;
`;

const PlaylistTile = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
    padding: 0.35rem;
    background-color: ${(props) => props.theme.SelectedBackgroundColor};
    border: ${(props) => props.theme.DefaultBorder};
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
    transition: box-shadow 0.2s ease;

    /* il long press del riordino non deve attivare selezione e callout iOS */
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;

    &:hover {
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    /* oscillazione su tutte le tile mentre un riordino è in corso */
    animation-name: ${(props) => props.$sorting && 'playlist-tile-wobble'};
    animation-iteration-count: infinite;
    animation-delay: -0.75s;
    animation-duration: 0.25s;
    transform-origin: 50% 10%;

    @keyframes playlist-tile-wobble {
        0% {
            transform: rotate(-0.75deg);
            animation-timing-function: ease-in;
        }

        50% {
            transform: rotate(0.75deg);
            animation-timing-function: ease-out;
        }
    }

    .playlist-tile-cover {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        aspect-ratio: 3 / 4;
        background-color: ${(props) => props.theme.BorderColor};
        cursor: ${(props) => (props.$previewable ? 'pointer' : 'default')};

        img {
            display: block;
            width: 100%;
            height: 100%;
            object-fit: cover;
            object-position: top center;
        }

        .playlist-tile-empty-cover {
            font-size: 2rem;
            color: ${(props) => props.theme.TextColorMuted};
            opacity: 0.6;
        }
    }

    .playlist-tile-type {
        position: absolute;
        z-index: 1;
        top: 6px;
        left: 6px;
        display: inline-flex;
        align-items: center;
        gap: 0.25rem;
        padding: 0.1rem 0.4rem;
        border-radius: 999px;
        background-color: rgba(0, 0, 0, 0.55);
        color: #fff;
        font-size: ${(props) => props.theme.FontSizeXSm};
        line-height: 1.5;
        text-transform: uppercase;
        pointer-events: none;
    }

    /* overlay di hover: il click resta gestito dalla cover sottostante */
    .playlist-tile-overlay {
        position: absolute;
        z-index: 2;
        inset: 0;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 0.35rem;
        padding: 0 0.4rem;
        background-color: ${(props) => `${props.theme.PrimaryColor}E6`};
        color: #fff;
        text-align: center;
        opacity: 0;
        transition: opacity 0.25s ease-in-out;
        pointer-events: none;

        svg {
            font-size: 1.3rem;
        }

        small {
            font-size: 0.65rem;
            line-height: 1.2;
            text-transform: uppercase;
        }
    }

    .playlist-tile-cover:hover .playlist-tile-overlay {
        opacity: 1;
    }

    .playlist-tile-footer {
        display: flex;
        align-items: flex-start;
        gap: 0.25rem;
        margin-top: 0.4rem;
    }

    .playlist-tile-title {
        flex: 1;
        min-width: 0;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        font-size: 0.75rem;
        font-weight: 600;
        line-height: 1.25;
        text-transform: uppercase;
        color: ${(props) => props.theme.TextColor};
        word-break: break-word;
    }

    .playlist-tile-edit {
        flex-shrink: 0;
        appearance: none;
        padding: 0.25rem;
        border: 0;
        border-radius: 4px;
        background: transparent;
        color: ${(props) => props.theme.TextColorMuted};
        line-height: 0;
        cursor: pointer;
        transition:
            color 0.15s ease,
            background 0.15s ease;

        &:hover {
            color: ${(props) => props.theme.PrimaryColor};
            background: rgba(0, 0, 0, 0.04);
        }

        @media only screen and (max-width: 720px) {
            padding: 0.4rem;
        }
    }
`;

/**
 * Tile di una playlist: cover (anteprima e presa per il drag), titolo e comandi.
 * `onPreview` e `onRemove` mancanti = versione in sola lettura.
 */
const PlaylistTileContent = ({ item, onEdit, onPreview, onRemove, removeButtonSize, showHint, sorting }) => {
    const cover = getBookCover(item);
    const isMedia = item.Type.toLowerCase() === 'media';

    const coverContent = (
        <div className="playlist-tile-cover" onClick={onPreview}>
            {cover ? (
                <img src={cover} alt="" />
            ) : (
                <span className="playlist-tile-empty-cover">
                    <NwIcon icon={byPrefixAndName.fasl['empty-set']} />
                </span>
            )}
            {isMedia && (
                <span className="playlist-tile-type">
                    <NwIcon icon={byPrefixAndName.fasl['folder-user']} /> media
                </span>
            )}
            {showHint && onPreview && (
                <div className="playlist-tile-overlay">
                    <NwIcon icon={byPrefixAndName.fasl['magnifying-glass-plus']} />
                    <small>
                        click to view
                        {onRemove && (
                            <>
                                <br />
                                hold and drag to sort
                            </>
                        )}
                    </small>
                </div>
            )}
        </div>
    );

    return (
        <PlaylistTile $previewable={Boolean(onPreview)} $sorting={sorting}>
            {onRemove ? (
                <NwTimesButton
                    ariaLabel="Remove from package"
                    offset={-removeButtonSize / 2}
                    onClick={onRemove}
                    size={removeButtonSize}
                >
                    {coverContent}
                </NwTimesButton>
            ) : (
                coverContent
            )}
            <div className="playlist-tile-footer">
                <span className="playlist-tile-title" title={item.Name}>
                    {item.Name}
                </span>
                <button
                    aria-label="Edit book"
                    className="playlist-tile-edit"
                    onClick={onEdit}
                    title="edit book"
                    type="button"
                >
                    <NwIcon icon={byPrefixAndName.fasl['pen']} />
                </button>
            </div>
        </PlaylistTile>
    );
};

const SortableItem = SortableElement(
    ({ isActivePackage, item, onBookSelect, onPreviewBook, packageId, removeButtonSize, showHint, sorting }) => {
        const { mutateAsync: onDeletePlaylistFromPackage } = useDeletePlaylistFromPackage();

        const packageContextValue = usePackageContext();
        const { afterUpdatePackage } = packageContextValue;

        const removeFromPackage = async () => {
            try {
                await onDeletePlaylistFromPackage({
                    packageID: packageId,
                    packagePlaylistId: item.ID,
                    isActivePackage: isActivePackage,
                });
                afterUpdatePackage();
            } catch (error) {
                console.error('Delete Playlist To Package Error => ', error);
            }
        };

        return (
            <PlaylistTileWrapper>
                <PlaylistTileContent
                    item={item}
                    onEdit={() => onBookSelect(item)}
                    onPreview={onPreviewBook}
                    onRemove={removeFromPackage}
                    removeButtonSize={removeButtonSize}
                    showHint={showHint}
                    sorting={sorting}
                />
            </PlaylistTileWrapper>
        );
    }
);

const SortableList = SortableContainer(
    ({
        isActivePackage,
        isMobile,
        items,
        onBookSelect,
        onPreviewBook,
        onShowMediaPreview,
        packageId,
        sorting,
        tileWidth,
    }) => {
        const removeButtonSize = isMobile ? REMOVE_BUTTON_SIZE_MOBILE : REMOVE_BUTTON_SIZE_DESKTOP;

        return (
            <PlaylistsGrid $tileWidth={tileWidth}>
                {items.map((item, index) => {
                    return (
                        <SortableItem
                            key={`item-${index}`}
                            index={index}
                            disabled={isMobile}
                            isActivePackage={isActivePackage}
                            item={item}
                            onBookSelect={onBookSelect}
                            onPreviewBook={() => {
                                if (item.Type.toLowerCase() === 'media') {
                                    onShowMediaPreview();
                                }
                                onPreviewBook(item.ID);
                            }}
                            packageId={packageId}
                            removeButtonSize={removeButtonSize}
                            showHint={!isMobile}
                            sorting={sorting}
                        />
                    );
                })}
            </PlaylistsGrid>
        );
    }
);

const PackagePlaylists = ({
    contactID,
    onSetIsUpdatingOrder,
    onUpdatePlaylistOrder,
    packageModelId,
    playlists,
    readOnly,
    smallView,
}) => {
    const [selectedPackagePlaylistID, setSelectedPackagePlaylistID] = useState();
    const [isBookDrawer, setIsBookDrawer] = useState(false);
    const [isSorting, setIsSorting] = useState(false);
    const { data: activePackages } = useActivePackages();
    const [selectedBook, setSelectedBook] = useState();
    const [showMediaPreview, setShowMediaPreview] = useState(false);

    const packageContextValue = usePackageContext();
    const { groupedPackage, afterUpdatePackage } = packageContextValue;
    const { ID: packageId } = groupedPackage;

    const layoutContext = useLayoutContext();
    const { isMobile } = layoutContext;

    const tileWidth = smallView ? 80 : isMobile ? 110 : 120;

    const onSortEnd = async ({ oldIndex, newIndex }) => {
        setIsSorting(false);
        if (oldIndex !== newIndex) {
            const newPlaylists = arrayMove(playlists, oldIndex, newIndex);
            onUpdatePlaylistOrder(newPlaylists);
            onSetIsUpdatingOrder(true);
            try {
                await Axios.put(
                    `/PackagePlaylists/SetOrder/${packageId}/${packageModelId}`,
                    newPlaylists.map((pl) => pl.ID)
                );
                afterUpdatePackage();
                onSetIsUpdatingOrder(false);
            } catch (error) {
                console.error('Update Playlist Order Error => ', error);
                onSetIsUpdatingOrder(false);
            }
        }
    };

    const onPreviewBook = (id) => {
        //setBookPreview(book);
        setSelectedPackagePlaylistID(id);
        setIsBookDrawer(true);
    };

    const onSelectBook = (book) => {
        setSelectedBook(book);
    };

    return (
        <>
            {readOnly ? (
                <PlaylistsGrid $tileWidth={tileWidth}>
                    {playlists.map((playlist) => {
                        return (
                            <PlaylistTileContent
                                key={`playlist-${playlist.ID}`}
                                item={playlist}
                                onEdit={() => onSelectBook(playlist)}
                            />
                        );
                    })}
                </PlaylistsGrid>
            ) : (
                <SortableList
                    axis="xy"
                    isActivePackage={activePackages && activePackages.find((pack) => pack.ID === packageId)}
                    isMobile={isMobile}
                    items={playlists}
                    onBookSelect={onSelectBook}
                    onPreviewBook={onPreviewBook}
                    onShowMediaPreview={() => setShowMediaPreview(true)}
                    onSortEnd={onSortEnd}
                    onSortStart={() => setIsSorting(true)}
                    packageId={packageId}
                    pressDelay={200}
                    shouldCancelStart={shouldCancelSortStart}
                    sorting={isSorting}
                    tileWidth={tileWidth}
                />
            )}

            {showMediaPreview && (
                <PackageMediaDrawer
                    packageplaylistid={selectedPackagePlaylistID}
                    onClose={() => {
                        setIsBookDrawer(false);
                        setShowMediaPreview(false);
                    }}
                />
            )}

            {!showMediaPreview && isBookDrawer && (
                <PackagePlayListDrawer
                    packageplaylistid={selectedPackagePlaylistID}
                    onClose={() => {
                        setIsBookDrawer(false);
                        setShowMediaPreview(false);
                    }}
                />
            )}

            {selectedBook && (
                <BookEditorDrawer
                    bookID={selectedBook.ID}
                    contactID={contactID}
                    packageID={packageId}
                    onClose={() => {
                        setSelectedBook();
                    }}
                />
            )}
        </>
    );
};

export default PackagePlaylists;
